import React from "react";
import { IEmployeesFilterParams } from "../../../common/controls/EmployeesComboboxMI";
import { createGridConfig, IGridResourcesConfig } from "../../../common/controls/Relations/GridResourcesConfig";
import WarningEmployeeChange from "../../../common/controls/Relations/WarningEmployeeChange";
import ResourcesAutocompleteMI, { OptionType } from "../../../common/controls/ResourcesAutocompleteMI";
import { IConfig } from "./Models";
import { IPersonProfileInsertData, PersonProfileInsertControl } from "./PersonProfileInsertControl";
import { IPersonProfileRelationData, IPersonProfileRelationRawData } from "./PersonProfileDomainModel";
import { IEmployeeEntity, RequestControl } from "../../../common/controls/Relations/RequestControl";
import Moment from "moment";
import { useTranslation } from "react-i18next";
import { RenderMultiEmployeesInput } from "../../../common/controls/Relations/RenderMultiEmployeesInput";
import AddScopeButton from "../../../common/controls/Relations/AddScopeButton";
import { ErrorMessageWithLabel } from "../../../common/controls/Relations/Components";
import lang from '../../../common/controls/Relations/RelationsLang';

interface IDictionary<TValue> {
	[id: number]: TValue;
}

export interface IPersonProfileInsertFormRef {
	hasChanges: () => boolean,
};

interface IPersonProfileInsertFormProps {
	selectedEmployees: OptionType[],
	onChangeSelectedEmployees: (v: OptionType[]) => void
	employeesFilters: IEmployeesFilterParams,

	selectedPersonProfiles: OptionType[],
	onChangeSelectedPersonProfiles: (v: OptionType[]) => void

	blockChangeEmployees: boolean;
	config: IConfig,
	blockInsert: boolean,

	table: React.RefObject<RequestControl>,
}

export const PersonProfileInsertForm = React.forwardRef((props: IPersonProfileInsertFormProps, ref: React.RefObject<IPersonProfileInsertFormRef>) => {
	const { t } = useTranslation();

	React.useImperativeHandle(ref, () => {
		let api: IPersonProfileInsertFormRef = {
			hasChanges: checkHasChanges,
		}
		return api;
	});

	const [employeesError, setEmployeesError] = React.useState("");
	const [personProfilesError, setPersonProfilesError] = React.useState("");
	const [warningEmployeeChange, setWarningEmployeeChange] = React.useState<OptionType>(null);
	const [hasChanges, setHasChanges] = React.useState(false);
	const [inputs, setInputs] = React.useState<IPersonProfileInsertData[]>([]);

	const [defaultPersonProfilesGrid, setDefaultPersonProfilesGrid] = React.useState<IGridResourcesConfig>(() => {
		return {
			title: t("selectPersonProfile"),
			hidden: false,
			columns: [
				{ id: "name", label: lang.name(), minWidth: 210, searched: true, numeric: false },
				{ id: "type", label: lang.type(), searched: false, numeric: false },
				{ id: "status", label: lang.status(), searched: true, numeric: false },
			],
			filters: "",
			rowsPerPageOptions: null,
		}
	});

	React.useEffect(() => {

		let copy = [...inputs];

		let newInputs: IPersonProfileInsertData[] = [];

		for (let i = 0; i < props.selectedPersonProfiles.length; i++) {
			let prof = props.selectedPersonProfiles[i];
			var exist = copy.filter(x => x.personProfileId == prof.value)[0];
			if (exist)
				newInputs.push(exist);
			else {
				newInputs.push({
					dateError: "",
					personProfileId: prof.value,
					personProfileName: prof.label,
					selectedFrom: null,
					selectedTo: null,
				})
			}
		}

		setInputs(newInputs);

	}, [props.selectedPersonProfiles]);

	function handleEmployeesChange(items: OptionType[]) {

		props.onChangeSelectedEmployees(items);
		setHasChanges(true);

		if (items.length == 0 || !items[0]) {
			// puste pole
			return;
		}

		if (props.config.fields.employee.multiple) {
			// pozwalamy na wielu pracownik�w
			return;
		}

		let item = items[0];
		let employeId = parseInt(item.value);
		let firstOrDefault = props.table.current?.searchSingle(x => x.employee?.id && x.employee.id != employeId);
		if (!firstOrDefault) {
			// we wniosku nie ma innych u�ytkownik�w
			return;
		}

		setWarningEmployeeChange(item);
	}

	function handlePersonProfileChange(values: OptionType[]) {
		props.onChangeSelectedPersonProfiles(values);
		setHasChanges(true);
	}

	function isValidAdd(employees: IDictionary<OptionType>) {
		let result = true;

		let employeesError = "";
		let resourcesError = "";

		if (Object.keys(employees).length == 0 && !props.config.fields.employee.hidden) {
			employeesError = "control.relation.requireErrorMessage";
			result = false;
		}
		if (props.selectedPersonProfiles.length == 0) {

			resourcesError = "control.relation.requireErrorMessage";
			result = false;
		}
		setEmployeesError(employeesError);
		setPersonProfilesError(resourcesError);

		inputs.forEach((v) => {
			if (v)
				result = result && isValidInput(v);
		});
		setInputs([...inputs]);

		return result;
	}

	function isValidInput(state: IPersonProfileInsertData): boolean {

		let result = true;
		state.dateError = "";
		if (props.config.fields.dateFrom.required && !state.selectedFrom) {
			const label: string = lang.getDefaultByPrefix(props.config.fields.dateFrom, "label", "from");
			state.dateError = t("control.relation.requireErrorMessage", { label });
			result = false;
		}

		if (props.config.fields.dateTo.required && !state.selectedTo) {
			const label: string = lang.getDefaultByPrefix(props.config.fields.dateTo, "label", "to");
			state.dateError = t("control.relation.requireErrorMessage", { label });
			result = false;
		}

		if (state.selectedFrom && !state.selectedFrom.isValid()) {
			result = false;
		}
		if (state.selectedTo && !state.selectedTo.isValid()) {
			result = false;
		}

		if (!result)
			return result;

		if (!state.selectedFrom) {
			state.selectedFrom = Moment();
		}

		if (state.selectedTo) {
			if (state.selectedFrom > state.selectedTo) {
				state.dateError = "control.relation.fromToErrorMessage";
				result = false;
			}
		}
		return result;
	}

	function handleAddClick() {

		let distinctOptions: IDictionary<OptionType> = {};
		for (var i = 0; i <= props.selectedEmployees.length; i++) {
			var emp = props.selectedEmployees[i];
			if (emp && !distinctOptions[emp.value]) {
				distinctOptions[emp.value] = emp;
			}
		}

		if (isValidAdd(distinctOptions)) {
			let itemsToPush: IPersonProfileRelationRawData[] = [];

			let uniqueEmployees = Object.values(distinctOptions) as OptionType[];

			if (uniqueEmployees.length == 0) {
				itemsToPush = createRelationByUser(null);
			}

			for (let i = 0; i < uniqueEmployees.length; i++) {
				let employeeEntity: IEmployeeEntity = null;
				employeeEntity = {
					id: parseInt(uniqueEmployees[i].value),
					name: uniqueEmployees[i].label,
					label: uniqueEmployees[i].label,
					kind: uniqueEmployees[i].kind,
					type: null,
				};
				var newRows = createRelationByUser(employeeEntity);

				itemsToPush = itemsToPush.concat(newRows);
			}

			props.table.current.push(itemsToPush);

			clear();
		}

		return false;
	}

	function createRelationByUser(employeeEntity: null | IEmployeeEntity): IPersonProfileRelationRawData[] {
		let itemsToPush: IPersonProfileRelationRawData[] = [];

		for (let j = 0; j < props.selectedPersonProfiles.length; j++) {
			let resource = props.selectedPersonProfiles[j];

			let addResource = pushResource(employeeEntity, resource);
			itemsToPush = itemsToPush.concat(addResource);
		}

		return itemsToPush;
	}

	function pushResource(employee: null | IEmployeeEntity, resource: OptionType): IPersonProfileRelationRawData[] {
		let itemsToPush: IPersonProfileRelationRawData[] = [];

		let values = inputs.filter(x => x.personProfileId == resource.value)[0];
		if (!values)
			return;

		var exist = _search(employee, resource);
		if (!exist) {
			itemsToPush.push({
				employee: employee,
				resource: {
					id: parseInt(resource.value),
					name: resource.label,
				},
				from: values.selectedFrom,
				to: values.selectedTo,
				errorMessages: [],
			});
		}
		return itemsToPush;
	}

	function _search(employee: IEmployeeEntity, resource: OptionType): IPersonProfileRelationData {
		let existRow = props.table.current.searchSingle((r) => {
			let row = r as IPersonProfileRelationData;
			let userAndResource = row.resource.id.toString() == resource.value;

			let employeEquels: boolean;
			if (employee)
				employeEquels = row.employee?.id == employee.id;
			else
				employeEquels = !row.employee;

			return userAndResource && employeEquels;
		});

		return existRow as IPersonProfileRelationData;
	}

	function renderInsertInput() {
		return <div>
			{inputs.map((dataSet, i) => {
				return <PersonProfileInsertControl
					key={dataSet.personProfileId}
					value={dataSet}
					onChange={(v) => {
						let copy = [...inputs];
						copy[i] = v;
						setInputs(copy);
					}}
					config={props.config}
					selectedEmployees={props.selectedEmployees}
				/>
			})}
		</div>
	}

	function clear() {
		props.onChangeSelectedPersonProfiles([]);
		setHasChanges(false);
	}

	function checkHasChanges(): boolean {

		if (props.selectedPersonProfiles.length == 0)
			return false;

		if (hasChanges)
			return true;

		return false;
	}

	function handleWarningAccept() {
		let rows = props.table.current.getRows();
		for (let i = 0; i < rows.length; i++) {
			let row = rows[i];
			row.deleted = true;
		}
		props.table.current.setRows(rows);
		setWarningEmployeeChange(null);
	}

	function handleWarningClose() {
		props.onChangeSelectedEmployees([null]);
		setWarningEmployeeChange(null);
	}


	function renderResourcesInputs() {
		const fields = props.config.fields;

		if (fields.personProfile.hidden)
			return null;

		let personProfileLabel = lang.getDefaultByPrefixFromFn(fields.personProfile, "label", () => t("control.relation.personProfiles"));

		return (<div className="flex-form-field form-field-access-for">
			<div className="flex-field-label">
				<label>{personProfileLabel}:</label>
			</div>
			<div className="flex-field-input" style={{ marginRight: "7px" }}>
				<ErrorMessageWithLabel
					resourcesError={personProfilesError}
					label={personProfileLabel}
				/>
				<ResourcesAutocompleteMI
					value={props.selectedPersonProfiles}
					multiple={fields.personProfile.multiple}

					onSelect={handlePersonProfileChange}
					kind="PersonProfile"
					filters={{
						maxRows: fields.personProfile.maxRows,
						filterId: fields.personProfile.filterId,
					}}
					placeholderText={lang.getDefaultByPrefix(fields.personProfile, "placeholder")}
					grid={null}
				/>
			</div>
		</div>);
	}

	if (props.blockInsert)
		return null;

	const fields = props.config.fields;

	return (<>
		<RenderMultiEmployeesInput
			blockChangeEmployees={props.blockChangeEmployees}
			config={fields.employee}
			employeesError={employeesError}
			employeesFilters={props.employeesFilters}
			name="selectPersonProfileFor"
			label="personProfileFor"
			onChange={(v) => handleEmployeesChange(v)}
			getButtons={(employee) => { return null; }}
			value={props.selectedEmployees}
		/>

		{renderResourcesInputs()}
		<div>
			{renderInsertInput()}
		</div>
		{!fields.personProfile.hidden && <AddScopeButton
			onClick={handleAddClick}
		/>}

		<WarningEmployeeChange
			warningEmployeeChange={warningEmployeeChange}
			onAccept={handleWarningAccept}
			onCancel={handleWarningClose}
		/>
	</>);
});